import ElakayImage from '../../../components/images/ElakayImage';
import React, { useState, useEffect, useCallback } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import { toImageUrl, getElakayImageLoadUrl } from "../../../config";
import imgProductLakay from '../../Layout/imgs/md3.jpg'
import Loader from "../../Layout/loader.gif";
import { Thumb } from './thmbsSlider'
import { getParamsAndQueriesCleaned } from '../../pageInside/ShowImage';

import './embla.css'
import { elakayAutoText } from "../../pageInside/dataElakay";


let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
const __elakayText = (key, fallback) =>
  __elakayInfoSource?.laguangeElakay?.("frontElakayHardcodedText", __elakayInfoSource.languageAPI)?.["slider"]?.[key] || fallback;

const EmblaSlideImage = ({ src, originalSrc, backgroundColor, infoSource, alt, priority = false }) => {
  __setElakayInfoSource(infoSource);
  const [isLoading, setIsLoading] = useState(true)

  return (
    <>
      {isLoading && (
        <div className="embla__slide__loader" style={{ backgroundColor }}>
          <img src={Loader} alt={__elakayText("t001", "Loading...")} />
        </div>
      )}
      <ElakayImage
        preset="product-detail"
        fallback={imgProductLakay}
        className="embla__slide__img"
        src={originalSrc || src}
        alt={alt || __elakayText("t002", "Product image")}
        style={{ backgroundColor }}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : undefined}
        decoding="async"
        onLoad={() => setIsLoading(false)}
        onError={() => setIsLoading(false)}
      />
    </>
  )
}

const EmblaCarousel = (props) => {
  const { slides: rawSlides, options, infoSource, title = "Product", previousLabel, nextLabel } = props
  const slides = Array.isArray(rawSlides) ? [...new Set(rawSlides.filter(Boolean))] : []
  const slidesKey = slides.join('|')
  __setElakayInfoSource(infoSource);
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [emblaMainRef, emblaMainApi] = useEmblaCarousel(options)

  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true
  })

  const onThumbClick = useCallback(
    (index) => {
      if (!emblaMainApi || !emblaThumbsApi) return
      emblaMainApi.scrollTo(index)
    },
    [emblaMainApi, emblaThumbsApi]
  )

  const onSelect = useCallback(() => {
    if (!emblaMainApi || !emblaThumbsApi) return
    setSelectedIndex(emblaMainApi.selectedScrollSnap())
    emblaThumbsApi.scrollTo(emblaMainApi.selectedScrollSnap())
  }, [emblaMainApi, emblaThumbsApi, setSelectedIndex])

  const scrollPrevious = useCallback(() => emblaMainApi?.scrollPrev(), [emblaMainApi])
  const scrollNext = useCallback(() => emblaMainApi?.scrollNext(), [emblaMainApi])

  useEffect(() => {
    if (!emblaMainApi) return
    onSelect()
    emblaMainApi.on('select', onSelect)
    emblaMainApi.on('reInit', onSelect)
    return () => {
      emblaMainApi.off('select', onSelect)
      emblaMainApi.off('reInit', onSelect)
    }
  }, [emblaMainApi, onSelect])

  useEffect(() => {
    if (!emblaMainApi) return
    emblaMainApi.reInit(options)
    emblaMainApi.scrollTo(0, true)
    setSelectedIndex(0)
  }, [emblaMainApi, options, slidesKey])

  const onKeyDown = (event) => {
    if (event.key === 'ArrowLeft') { event.preventDefault(); scrollPrevious() }
    if (event.key === 'ArrowRight') { event.preventDefault(); scrollNext() }
  }

  return (
    <div className="embla" tabIndex={slides.length > 1 ? 0 : undefined} onKeyDown={onKeyDown} aria-label={elakayAutoText("h_17eba6cb75049a", "{value0} image gallery", { value0: title })}>
      <div className="embla__viewport" ref={emblaMainRef}>
        <div className="embla__container">
          {slides?.map((slide, i) => {
            const colorBackground = getParamsAndQueriesCleaned(slide)?.query?.bgc || '#F5F5F5'
            const originalSrc = toImageUrl(slide)
            return (
              <div className="embla__slide" key={`${i}_${slide}`}>
                {(i === selectedIndex) && <EmblaSlideImage key={originalSrc} priority={i === 0} src={getElakayImageLoadUrl(originalSrc, { width: 900 })} originalSrc={originalSrc} backgroundColor={colorBackground} infoSource={infoSource} alt={`${title} — ${i + 1}/${slides.length}`} />}
              </div>
            )
          })}
        </div>
      </div>

      {slides.length > 1 && <>
        <button type="button" className="embla__arrow embla__arrow--prev" onClick={scrollPrevious} aria-label={previousLabel || elakayAutoText("h_46edf0f8b4d679", "Previous image")}>‹</button>
        <button type="button" className="embla__arrow embla__arrow--next" onClick={scrollNext} aria-label={nextLabel || elakayAutoText("h_eb89a789f14712", "Next image")}>›</button>
        <span className="embla__counter" aria-live="polite">{selectedIndex + 1}/{slides.length}</span>
      </>}

      {slides?.length > 1 && (
        <div className="embla-thumbs">
          <div className="embla-thumbs__viewport" ref={emblaThumbsRef}>
            <div className="embla-thumbs__container">
              {slides?.map((slide, i) => (
                <Thumb
                  onClick={() => onThumbClick(i)}
                  selected={i === selectedIndex}
                  index={i}
                  imgSrc={slide}
                  infoSource={infoSource}
                  alt={`${title} — ${i + 1}`}
                  key={`${i}_${slide}`}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Named export purely for unit-testing the optimized/original/placeholder
// image fallback chain in isolation, without mounting the full embla
// carousel (see slider.imgFallback.test.jsx).
export { EmblaSlideImage }
export default EmblaCarousel
